import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { from, Observable, of } from 'rxjs';
import { UserService } from 'app/services/user.service';
import { UserFlagsService } from 'app/services/user-flags.service';
import { SubscriptionService } from 'app/services/subscriptions.service';
import { AppStateService } from 'app/services/app-state.service';

function isIndependentMembership(membership: any): boolean {
  return membership?.provider === 'independent' || membership?.organization === 'independent';
}

function hasMembershipAccess(membership: any): boolean {
  return Boolean(
    membership?.statementVerified === true ||
    membership?.statementAccessGranted === true ||
    membership?.trustedContact === true ||
    isIndependentMembership(membership)
  );
}

function hasHardRequiredMembership(memberships: any[]): boolean {
  return memberships.some(m => m?.statementRequiresHardReview === true || m?.statementStatus === 'hard_required');
}

function hasAccessMembership(memberships: any[]): boolean {
  return memberships.some(hasMembershipAccess);
}

function isTrustedContactProfile(profile: any): boolean {
  return profile?.accountType === 'trusted-contact' || Boolean(profile?.trustedContactInvite);
}

function hasVerifiedIdentity(profile: any): boolean {
  return profile?.isHumanVerified === true || profile?.idv?.status === 'verified';
}

function canBypassHardReview(profile: any, memberships: any[]): boolean {
  return isTrustedContactProfile(profile) || memberships.some(m => m?.trustedContact === true || isIndependentMembership(m));
}

export const onboardGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const userService = inject(UserService);
  const flagsService = inject(UserFlagsService);
  const router = inject(Router);

  return authService.user$.pipe(
    filter(user => user !== undefined),
    take(1),
    switchMap(user => {
      if (!user) {
        router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
        return of(false);
      }

      return userService.userProfile$.pipe(
        filter(profile => profile !== null),
        take(1),
        switchMap(profile => {
          const memberships = Array.isArray(profile.memberships) ? profile.memberships : [];
          const hasAccess = hasAccessMembership(memberships);
          const hardReviewRequired = hasHardRequiredMembership(memberships) && !canBypassHardReview(profile, memberships);
          const trustedContact = isTrustedContactProfile(profile);
          const verifiedIdentity = hasVerifiedIdentity(profile);
          const currentPath = state.url.split(/[?#]/)[0];

          if (trustedContact) {
            if (currentPath === '/onboard/invited-party/aircraft' && !verifiedIdentity) {
              router.navigate(['/onboard/invited-party/verify']);
              return of(false);
            }

            if (currentPath === '/onboard/invited-party/verify' && verifiedIdentity) {
              router.navigate(['/onboard/invited-party/aircraft']);
              return of(false);
            }
          }

          if (state.url === '/onboard/get-started') {
            if (hardReviewRequired) {
              router.navigate(['/onboard/membership/verify', memberships[0].id]);
              return of(false);
            }

            if (!hasAccess) {
              if (trustedContact) {
                router.navigate(['/onboard/invited-party/verify']);
                return of(false);
              }

              if (memberships.length > 0) {
                router.navigate(['/onboard/membership/verify', memberships[0].id]);
              } else {
                router.navigate(['/onboard/membership']);
              }
              return of(false);
            }

            return flagsService.flags$.pipe(
              take(1),
              map(flags => {
                if (flags.hasCompletedOnboardEducation) {
                  return router.createUrlTree(['/agent']);
                }
                return true;
              })
            );
          }

          if (state.url === '/onboard/verify-phone') {
            if (profile.mobileNumber && profile.isMobileVerified) {
              router.navigate(['/onboard/membership']);
              return of(false);
            }
          }

          if (state.url === '/onboard/account-type') {
            if (profile.accountType) {
              router.navigate(['/onboard/membership']);
              return of(false);
            }
          }

          if (state.url === '/onboard/membership') {
            if (trustedContact) {
              router.navigate(['/onboard/invited-party/verify']);
              return of(false);
            }

            if (hardReviewRequired) {
              router.navigate(['/onboard/membership/verify', memberships[0].id]);
              return of(false);
            }

            if (hasAccess) {
              router.navigate(['/onboard/get-started']);
              return of(false);
            }
          }

          return of(true);
        })
      );
    })
  );
};

export const profileGuard: CanActivateFn = (_route, state): Observable<boolean | UrlTree> => {
  const authService = inject(AuthService);
  const userService = inject(UserService);
  const router = inject(Router);
  const returnUrl = state.url;

  return authService.user$.pipe(
    filter(u => u !== undefined),
    take(1),
    switchMap(user => {
      if (!user) {
        return of(
          router.createUrlTree(['/login'], { queryParams: { returnUrl } })
        );
      }

      return userService.userProfile$.pipe(
        filter(p => p !== null),
        take(1),
        map(profile => {
          if (!profile.mobileNumber || !profile.isMobileVerified) {
            return router.createUrlTree(
              ['/onboard/verify-phone'],
              { queryParams: { returnUrl } }
            );
          }

          if (!Array.isArray(profile.memberships) || profile.memberships.length === 0) {
            if (isTrustedContactProfile(profile)) {
              return router.createUrlTree(
                ['/onboard/invited-party/verify'],
                { queryParams: { returnUrl } }
              );
            }

            return router.createUrlTree(
              ['/onboard/account-type'],
              { queryParams: { returnUrl } }
            );
          }

          const memberships = profile.memberships;
          const hardReviewRequired = hasHardRequiredMembership(memberships) && !canBypassHardReview(profile, memberships);
          if (hardReviewRequired) {
            const first = memberships[0];
            return router.createUrlTree(
              ['/onboard/membership/verify', first.id],
              { queryParams: { returnUrl } }
            );
          }

          if (!hasAccessMembership(memberships)) {
            const first = memberships[0];
            return router.createUrlTree(
              ['/onboard/membership/verify', first.id],
              { queryParams: { returnUrl } }
            );
          }

          return true;
        })
      );
    })
  );
};

export const subscriptionGuard: CanActivateFn = (_route, state) => {
  const authService = inject(AuthService);
  const userService = inject(UserService);
  const subscriptionService = inject(SubscriptionService);
  const appState = inject(AppStateService);
  const router = inject(Router);

  return authService.user$.pipe(
    filter(user => user !== undefined),
    take(1),
    switchMap(user => {
      if (!user) {
        router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
        return of(false);
      }

      return userService.userProfile$.pipe(
        filter(profile => profile !== null),
        take(1),
        switchMap(profile => {
          if (!profile?.id) {
            return of(router.createUrlTree(['/subscribe'], { queryParams: { returnUrl: state.url } }));
          }

          const cachedSubscription = appState.user.subscription();
          const subscriptionPromise = cachedSubscription
            ? Promise.resolve(cachedSubscription)
            : subscriptionService.getSubscriptionInfo();

          return from(subscriptionPromise).pipe(
            switchMap(subscription => {
              if (!cachedSubscription) {
                appState.user.subscription.set(subscription);
              }

              const cachedEntitlement = appState.user.entitlement();
              const needsRefresh = cachedEntitlement?.userId !== profile.id;

              return from(
                subscriptionService.getEntitlementState(profile, {
                  subscription,
                  forceRefresh: needsRefresh
                })
              ).pipe(
                map(entitlement => {
                  appState.user.entitlement.set(entitlement);
                  if (entitlement.status === 'inactive') {
                    return router.createUrlTree(['/subscribe'], { queryParams: { returnUrl: state.url } });
                  }
                  return true;
                })
              );
            })
          );
        })
      );
    })
  );
};

export const authGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const userService = inject(UserService);
  const router = inject(Router);

  return authService.user$.pipe(
    filter(user => user !== undefined),
    take(1),
    switchMap(user => {
      if (!user) {
        router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
        return of(false);
      }

      return userService.userProfile$.pipe(
        filter(profile => profile !== null),
        take(1),
        map(profile => {
          if (profile.isMobileVerified) {
            return true;
          }

          router.navigate(['onboard/verify-phone'], { queryParams: { returnUrl: state.url } });
          return false;
        })
      );
    })
  );
};

export const noAuthGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  return authService.user$.pipe(
    filter(user => user !== undefined),
    take(1),
    map(user => {
      if (!user) {
        return true;
      }

      router.navigate(['/agent']);
      return false;
    })
  );
};

export const adminGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const userService = inject(UserService);
  const router = inject(Router);

  return authService.user$.pipe(
    filter(user => user !== undefined),
    take(1),
    switchMap(user => {
      if (!user) {
        router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
        return of(false);
      }

      return userService.userProfile$.pipe(
        filter(profile => profile !== null),
        take(1),
        map(profile => {
          if (profile?.palermo) {
            return true;
          }

          router.navigate(['/agent']);
          return false;
        })
      );
    })
  );
};
